import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useTranslation, Trans } from "react-i18next";
import FastResearchChat from "./FastResearchChat";

// Keyframes injected once at module load, OUTSIDE React's reconciler.
// React 18 auto-hoists <style> tags from JSX to <head>; when the tree unmounts,
// React tries to remove them from their original parent and fails with
// "Failed to execute 'removeChild'". Keeping these in <head> ourselves avoids that.
(() => {
  if (typeof document === 'undefined') return;
  const id = 'fr-ultra-progress-keyframes';
  if (document.getElementById(id)) return;
  const el = document.createElement('style');
  el.id = id;
  el.textContent = `
    @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
    @keyframes smoothScroll { 0% { transform: translateY(0); } 100% { transform: translateY(-100%); } }
    @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes slideDown { from { transform: translateY(-100%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
    @keyframes slowScroll { 0% { transform: translateY(0); } 100% { transform: translateY(-50%); } }
    @keyframes pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.1); } }
    @keyframes softFade { 0% { opacity: 0; } 20% { opacity: 1; } 80% { opacity: 1; } 100% { opacity: 0; } }
    .interview-questions-scroll::-webkit-scrollbar { display: none; }
    .interview-questions-scroll { -ms-overflow-style: none; scrollbar-width: none; }
  `;
  document.head.appendChild(el);
})();

// Display a stored websiteUrl cleanly whether it's a real URL or a free-text topic.
// Strips http(s):// and trailing slash; leaves topics alone.
function displayWebsiteOrTopic(value) {
  if (!value) return '';
  return String(value).replace(/^https?:\/\//i, '').replace(/\/$/, '');
}

// Mobile attention nudge: short sine beep + Android vibrate.
// iOS silently ignores vibrate; both stays quiet if no audio context yet.
function notifyUser({ frequency = 880, vibratePattern = 30, gain = 0.08, durationMs = 250 } = {}) {
  if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
    try { navigator.vibrate(vibratePattern); } catch (_) { /* iOS / disallowed */ }
  }
  if (typeof window === 'undefined') return;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return;
  try {
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = frequency;
    g.gain.value = gain;
    osc.connect(g);
    g.connect(ctx.destination);
    osc.start();
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + durationMs / 1000);
    osc.stop(ctx.currentTime + (durationMs + 50) / 1000);
    setTimeout(() => ctx.close().catch(() => {}), durationMs + 200);
  } catch (_) { /* audio blocked */ }
}

function formatChatAccessLeft(expiresAt, t) {
  if (!expiresAt) return null;
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (!Number.isFinite(ms) || ms <= 0) return null;
  const days = Math.floor(ms / 86400000);
  if (days >= 1) return t('progress.chatLeft.days', { count: days, defaultValue: '{{count}} days of chat left' });
  const hours = Math.floor(ms / 3600000);
  if (hours >= 1) return t('progress.chatLeft.hours', { count: hours, defaultValue: '{{count}} hours of chat left' });
  const minutes = Math.max(0, Math.floor(ms / 60000));
  return t('progress.chatLeft.minutes', { count: minutes, defaultValue: '{{count}} minutes of chat left' });
}

function ProgressTracker() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [currentStatus, setCurrentStatus] = useState("");
  const [researchData, setResearchData] = useState(null);
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [currentResponseIndex, setCurrentResponseIndex] = useState(0);
  const [currentSurveyResponseIndex, setCurrentSurveyResponseIndex] = useState(0);
  const [showBanner, setShowBanner] = useState(true);
  const [focusedStep, setFocusedStep] = useState(null); // null = auto-focus current step
  const wakeLockRef = useRef(null);
  const { t } = useTranslation('fastresearch');

  // Auto-dismiss the email banner after 10s — it's a confirmation nudge, not a persistent one
  useEffect(() => {
    if (!showBanner || !email) return;
    const timer = setTimeout(() => setShowBanner(false), 10000);
    return () => clearTimeout(timer);
  }, [showBanner, email]);

  // Ping once when the report finishes — pulls the user back if they put the phone down
  const reportReadyPingedRef = useRef(false);
  useEffect(() => {
    if (reportReadyPingedRef.current) return;
    if (researchData?.progress?.pdf === 'complete') {
      reportReadyPingedRef.current = true;
      notifyUser({ frequency: 880, vibratePattern: [40, 60, 40], gain: 0.1, durationMs: 280 });
    }
  }, [researchData?.progress?.pdf]);

  const getApiBaseURL = () => {
    return process.env.NODE_ENV === "production"
      ? process.env.REACT_APP_BACKEND_URL_PROD
      : process.env.REACT_APP_BACKEND_URL_DEV;
  };

  // Global Wake Lock for ProgressTracker (can be accessed directly via URL)
  useEffect(() => {
    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator && !wakeLockRef.current) {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
          console.log('Progress Tracker: Wake Lock acquired');
        }
      } catch (err) {
        console.log('Progress Tracker: Wake Lock error:', err);
      }
    };

    const releaseWakeLock = async () => {
      if (wakeLockRef.current !== null) {
        try {
          await wakeLockRef.current.release();
          wakeLockRef.current = null;
          console.log('Progress Tracker: Wake Lock released');
        } catch (err) {
          console.log('Progress Tracker: Wake Lock release error:', err);
        }
      }
    };

    // Acquire wake lock on mount
    requestWakeLock();

    // Periodic activity to prevent aggressive screen timeouts
    const intervalId = setInterval(() => {
      if (document.hidden === false) {
        document.body.style.opacity = document.body.style.opacity === '1' ? '0.99999' : '1';
      }
    }, 5000);

    // Cleanup on unmount
    return () => {
      clearInterval(intervalId);
      releaseWakeLock();
    };
  }, []);

  // Re-acquire wake lock when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && !wakeLockRef.current) {
        try {
          if ('wakeLock' in navigator) {
            wakeLockRef.current = await navigator.wakeLock.request('screen');
            console.log('Progress Tracker: Wake Lock re-acquired');
          }
        } catch (err) {
          console.log('Progress Tracker: Wake Lock re-acquire error:', err);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
    };
  }, []);

  // Poll for research progress
  useEffect(() => {
    if (!id) {
      setError(t('progress.noResearchId'));
      return;
    }

    const pollProgress = async () => {
      try {
        const response = await axios.get(`${getApiBaseURL()}/fast-research/${id}`);
        if (response.data.success) {
          const research = response.data.research;
          setResearchData(research);
          setEmail(research.email || "");

          // Update browser tab title based on currentStage
          document.title = t(`progress.tabTitle.${research.currentStage}`, { defaultValue: t('progress.tabTitle.processing') });

          // Use percentComplete for progress indication
          setCurrentStatus(`${research.percentComplete || 0}% complete`);
        }
      } catch (err) {
        console.error("Error polling research progress:", err);
        if (err.response?.status === 404) {
          setError(t('progress.researchNotFound'));
        }
      }
    };

    // Poll immediately, then every 3 seconds for live updates
    pollProgress();
    const interval = setInterval(pollProgress, 3000);

    return () => {
      clearInterval(interval);
      document.title = 'Yatabase'; // Reset title
    };
  }, [id]);

  // Rotate through persona responses every 7 seconds
  useEffect(() => {
    const hasPreview = researchData?.interview?.previewInterview?.responses?.length > 0;

    if (hasPreview) {
      const totalResponses = researchData.interview.previewInterview.responses.length;

      const rotationInterval = setInterval(() => {
        setCurrentResponseIndex((prev) => {
          const next = (prev + 1) % totalResponses;
          console.log(`Rotating to response ${next + 1}/${totalResponses}`);
          return next;
        });
      }, 10000);

      return () => clearInterval(rotationInterval);
    }
  }, [researchData?.interview?.previewAvailable, researchData?.interview?.previewInterview?.responses?.length]);

  // Rotate through survey responses every 10 seconds
  useEffect(() => {
    const hasSurveyPreview = researchData?.survey?.preview?.sampleResponses?.length > 0;

    if (hasSurveyPreview) {
      const totalResponses = researchData.survey.preview.sampleResponses.length;

      const rotationInterval = setInterval(() => {
        setCurrentSurveyResponseIndex((prev) => {
          const next = (prev + 1) % totalResponses;
          return next;
        });
      }, 10000);

      return () => clearInterval(rotationInterval);
    }
  }, [researchData?.survey?.preview?.sampleResponses?.length]);

  if (error) {
    return (
      <div style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px"
      }}>
        <div style={{
          maxWidth: "600px",
          width: "100%",
          textAlign: "center"
        }}>
          <h1 style={{ fontSize: "28px", marginBottom: "20px", color: "#ef4444" }}>
            {"❌ " + t('progress.error')}
          </h1>
          <p style={{ fontSize: "17px", color: "#666" }}>
            {error}
          </p>
          <button
            onClick={() => navigate('/ultrafastresearch')}
            style={{
              marginTop: "20px",
              padding: "12px 24px",
              fontSize: "16px",
              border: "2px solid #333",
              borderRadius: "8px",
              backgroundColor: "rgba(255, 255, 255, 0.08)",
              cursor: "pointer"
            }}
          >
            {t('progress.startNewResearch')}
          </button>
        </div>
      </div>
    );
  }

  if (!researchData) {
    return (
      <div style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}>
        <div style={{ textAlign: "center" }}>
          <div style={{
            width: "50px",
            height: "50px",
            border: "4px solid #e5e7eb",
            borderTop: "4px solid #333",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
            margin: "0 auto 20px"
          }}></div>
          <p style={{ fontSize: "17px", color: "#666" }}>{t('progress.loadingResearch')}</p>
        </div>
      </div>
    );
  }

  // Map progress object to UI display
  const progressSteps = [
    {
      key: 'personas',
      label: (() => {
        const status = researchData?.progress?.personas || 'pending';
        if (status === 'complete') return t('progress.personas.complete');
        if (status === 'in_progress') return t('progress.personas.inProgress');
        return t('progress.personas.pending');
      })(),
      detail: (() => {
        const status = researchData?.progress?.personas || 'pending';
        const count = researchData?.personas?.length || 5;
        if (status === 'complete') return t('progress.personas.detailComplete', { count });
        if (status === 'in_progress') return t('progress.personas.detailInProgress', { count });
        return t('progress.personas.detailPending');
      })(),
      status: researchData?.progress?.personas || 'pending'
    },
    {
      key: 'interview',
      label: (() => {
        const status = researchData?.progress?.interview || 'pending';
        if (status === 'complete') return t('progress.interview.complete');
        if (status === 'in_progress') return t('progress.interview.inProgress');
        return t('progress.interview.pending');
      })(),
      detail: (() => {
        const status = researchData?.progress?.interview || 'pending';
        const framework = researchData?.interview?.frameworkName;
        const questionCount = researchData?.interview?.questions?.length || 10;
        const personaCount = researchData?.personas?.length || 5;

        if (status === 'complete') {
          return framework
            ? t('progress.interview.detailCompleteFramework', { questionCount, personaCount, framework })
            : t('progress.interview.detailComplete', { questionCount, personaCount });
        }
        if (status === 'in_progress') {
          return framework
            ? t('progress.interview.detailInProgressFramework', { framework })
            : t('progress.interview.detailInProgress');
        }
        return t('progress.interview.detailPending');
      })(),
      status: researchData?.progress?.interview || 'pending'
    },
    {
      key: 'survey',
      label: (() => {
        const status = researchData?.progress?.survey || 'pending';
        if (status === 'complete') return t('progress.survey.complete');
        if (status === 'in_progress') return t('progress.survey.inProgress');
        return t('progress.survey.pending');
      })(),
      detail: (() => {
        const status = researchData?.progress?.survey || 'pending';
        const framework = researchData?.survey?.metadata?.selectedFramework?.name;
        const questionCount = researchData?.survey?.questions?.length ||
                            researchData?.survey?.metadata?.questionCount ||
                            0;
        const responseCount = researchData?.survey?.responses?.length || 50;

        if (status === 'complete') {
          return framework
            ? t('progress.survey.detailCompleteFramework', { questionCount, responseCount, framework })
            : t('progress.survey.detailComplete', { questionCount, responseCount });
        }
        if (status === 'in_progress') {
          return framework
            ? t('progress.survey.detailInProgressFramework', { framework })
            : t('progress.survey.detailInProgress');
        }
        return t('progress.survey.detailPending');
      })(),
      status: researchData?.progress?.survey || 'pending'
    },
    {
      key: 'metaAnalysis',
      label: (() => {
        const status = researchData?.progress?.metaAnalysis || 'pending';
        if (status === 'complete') return t('progress.metaAnalysis.complete');
        if (status === 'in_progress') return t('progress.metaAnalysis.inProgress');
        return t('progress.metaAnalysis.pending');
      })(),
      detail: (() => {
        const status = researchData?.progress?.metaAnalysis || 'pending';
        if (status === 'complete') return t('progress.metaAnalysis.detailComplete');
        if (status === 'in_progress') return t('progress.metaAnalysis.detailInProgress');
        return t('progress.metaAnalysis.detailPending');
      })(),
      status: researchData?.progress?.metaAnalysis || 'pending'
    },
    {
      key: 'pdf',
      label: (() => {
        const status = researchData?.progress?.pdf || 'pending';
        if (status === 'complete') return t('progress.pdf.complete');
        if (status === 'in_progress') return t('progress.pdf.inProgress');
        return t('progress.pdf.pending');
      })(),
      detail: (() => {
        const status = researchData?.progress?.pdf || 'pending';
        if (status === 'complete') return t('progress.pdf.detailComplete');
        if (status === 'in_progress') return t('progress.pdf.detailInProgress');
        return t('progress.pdf.detailPending');
      })(),
      status: researchData?.progress?.pdf || 'pending'
    }
  ];

  return (
    <>
      <div style={{
        // `100dvh` tracks the current visible viewport (excludes Safari's address bar when it's showing).
        // Previously `minHeight: 100vh` included the address-bar area, which made the outer ~100px taller
        // than the inner chat (which uses 100dvh) — leaving dead space below the input on iOS.
        height: "100dvh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "1.25rem",
        backgroundColor: "#000",
        position: "relative",
        overflow: "auto",
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
      }}>
        {/* Dismissible Banner Overlay — only during generation */}
      {showBanner && email && researchData?.progress?.pdf !== "complete" && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          backgroundColor: "#6366f1",
          color: "white",
          padding: "16px 20px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          zIndex: 1000,
          animation: "slideDown 0.3s ease"
        }}>
          <div style={{
            maxWidth: "900px",
            margin: "0 auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "20px"
          }}>
            <div style={{ flex: 1 }}>
              <div style={{
                fontSize: "14px",
                fontWeight: "500"
              }}>
                <Trans i18nKey="progress.emailBanner" ns="fastresearch" values={{ email }} components={{ strong: <strong /> }} />
              </div>
            </div>
            <button
              onClick={() => setShowBanner(false)}
              style={{
                background: "rgba(255,255,255,0.2)",
                border: "none",
                borderRadius: "50%",
                width: "32px",
                height: "32px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                fontSize: "18px",
                color: "white",
                flexShrink: 0,
                transition: "background 0.2s"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.3)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.2)";
              }}
            >
              ✕
            </button>
          </div>
        </div>
      )}



      {/* Chat-primary layout — active once personas are generated. */}
      {researchData?.progress?.personas === "complete" && (
        <div style={{
          width: "100%",
          maxWidth: "1200px",
          // Explicit height so inner flex:1 chat actually fills.
          // Subtracts: container padding (2.5rem = 40px top+bottom) and the
          // fixed email banner (80px, when shown).
          height: `calc(100dvh - 40px${showBanner && email && researchData?.progress?.pdf !== "complete" ? " - 80px" : ""})`,
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          marginTop: showBanner && email && researchData?.progress?.pdf !== "complete" ? "80px" : "0",
          transition: "margin-top 0.3s ease, height 0.3s ease"
        }}>
          {/* Compact stage bar — clickable when report is ready */}
          <div
            onClick={researchData?.progress?.pdf === "complete"
              ? () => navigate(`/ultrafastresearch/${id}/results`)
              : undefined}
            style={{
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              gap: "10px",
              padding: "10px 14px",
              backgroundColor: researchData?.progress?.pdf === "complete"
                ? "rgba(167, 139, 250, 0.15)"
                : "rgba(255,255,255,0.04)",
              border: researchData?.progress?.pdf === "complete"
                ? "1px solid rgba(167, 139, 250, 0.4)"
                : "1px solid rgba(255,255,255,0.08)",
              borderRadius: "10px",
              color: "#fff",
              fontSize: "13px",
              cursor: researchData?.progress?.pdf === "complete" ? "pointer" : "default",
              transition: "background-color 0.15s"
            }}>
            {researchData?.upgraded && researchData?.progress?.pdf === "complete" ? (
              // Upgraded + report ready: show chat access countdown
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
                <div style={{ fontWeight: 500, whiteSpace: "nowrap", flex: 1 }}>
                  {formatChatAccessLeft(
                    researchData?.personaChat?.accessExpiresAt
                      || (researchData?.upgradedAt
                        ? new Date(new Date(researchData.upgradedAt).getTime() + 7 * 24 * 60 * 60 * 1000).toISOString()
                        : null),
                    t
                  ) || t('progress.stageLabel.complete', 'Report ready')}
                </div>
                <div style={{ fontSize: "12px", fontWeight: 500, color: "#a78bfa", whiteSpace: "nowrap" }}>
                  {t('personaChat.viewReport', 'View report →')}
                </div>
              </>
            ) : (
              // Generating: stage label + progress bar
              <>
                <div style={{ opacity: 0.6, fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.06em", whiteSpace: "nowrap" }}>
                  {t('progress.stageLabel.prefix', 'Now')}
                </div>
                <div style={{ fontWeight: 500, whiteSpace: "nowrap" }}>
                  {t(`progress.stageLabel.${researchData?.currentStage || 'interview'}`, {
                    defaultValue: {
                      personas: 'Generating personas',
                      interview: 'Conducting interviews',
                      survey: 'Running survey',
                      metaAnalysis: 'Analyzing findings',
                      pdf: 'Finalizing report',
                      complete: 'Report ready'
                    }[researchData?.currentStage || 'interview'] || 'Working on your report'
                  })}
                </div>
                <div style={{
                  flex: 1,
                  height: "4px",
                  borderRadius: "2px",
                  backgroundColor: "rgba(255,255,255,0.08)",
                  overflow: "hidden",
                  minWidth: "60px"
                }}>
                  <div style={{
                    width: `${Math.max(0, Math.min(100, researchData?.percentComplete || 0))}%`,
                    height: "100%",
                    backgroundColor: "#a78bfa",
                    transition: "width 0.6s ease"
                  }} />
                </div>
                {researchData?.progress?.pdf === "complete" ? (
                  <div style={{ fontSize: "12px", fontWeight: 500, color: "#a78bfa", whiteSpace: "nowrap" }}>
                    {t('personaChat.viewReport', 'View report →')}
                  </div>
                ) : (
                  <div style={{ opacity: 0.7, fontSize: "11px", minWidth: "34px", textAlign: "right" }}>
                    {Math.round(researchData?.percentComplete || 0)}%
                  </div>
                )}
              </>
            )}
          </div>

          {/* Chat fills remaining height */}
          <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
            <FastResearchChat researchId={id} language={researchData?.language} />
          </div>
        </div>
      )}

      {researchData?.progress?.personas !== "complete" && (
      <div style={{
        maxWidth: "600px",
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        marginTop: showBanner && email && researchData?.progress?.pdf !== "complete" ? "80px" : "0",
        transition: "margin-top 0.3s ease"
      }}>
        {/* Header - Fixed Height */}
        <div style={{ flexShrink: 0 }}>
          <h1 style={{
            fontSize: "22px",
            marginBottom: "6px",
            fontWeight: "700",
            textAlign: "center",
            color: "#fff"
          }}>
            {t('progress.researchInProgress')}
          </h1>

          <p style={{
            fontSize: "13px",
            color: "#888",
            textAlign: "center",
            marginBottom: "16px"
          }}>
            {displayWebsiteOrTopic(researchData?.websiteUrl)}
          </p>

          {/* Progress bar */}
          <div style={{
            width: "100%",
            height: "6px",
            backgroundColor: "#e2e8f0",
            borderRadius: "3px",
            overflow: "hidden",
            marginBottom: "8px"
          }}>
            <div style={{
              width: `${researchData?.percentComplete || 0}%`,
              height: "100%",
              backgroundColor: "#6366f1",
              transition: "width 0.5s ease",
              boxShadow: "0 0 8px rgba(59, 130, 246, 0.4)"
            }}></div>
          </div>

          <p style={{
            fontSize: "12px",
            color: "#888",
            textAlign: "center",
            marginBottom: "20px",
            fontWeight: "500"
          }}>
            {researchData?.percentComplete || 0}% {t('progress.complete')}
          </p>
        </div>

        {/* Step Badges - Compact row showing all steps */}
        <div style={{
          display: "flex",
          gap: "8px",
          marginBottom: "16px",
          flexShrink: 0,
          overflowX: "auto",
          paddingBottom: "4px"
        }}>
          {progressSteps.map((item) => {
            const isComplete = item.status === 'complete';
            const isCurrent = item.status === 'in_progress';
            const isFailed = item.status === 'failed';
            const currentKey = focusedStep || (progressSteps.find(s => s.status === 'in_progress')?.key || progressSteps[0].key);
            const isFocused = item.key === currentKey;

            return (
              <div
                key={item.key}
                onClick={() => setFocusedStep(item.key)}
                style={{
                  flex: "0 0 auto",
                  padding: "8px 12px",
                  borderRadius: "8px",
                  backgroundColor: isFocused ? "#6366f1" : (isComplete ? "#ecfdf5" : "#f8fafc"),
                  border: isFocused ? "2px solid #6366f1" : `1px solid ${isComplete ? "#6ee7b7" : "#e2e8f0"}`,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px"
                }}
              >
                <div style={{
                  width: "18px",
                  height: "18px",
                  borderRadius: "50%",
                  backgroundColor: isFocused ? "rgba(255, 255, 255, 0.3)" : (isFailed ? "#ef4444" : isComplete ? "#a78bfa" : "#e2e8f0"),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "10px",
                  color: (isFocused || isComplete || isFailed) ? "white" : "#888",
                  flexShrink: 0
                }}>
                  {isFailed ? "✗" : isComplete ? "✓" : isCurrent ? "⟳" : "○"}
                </div>
                <span style={{
                  fontSize: "12px",
                  fontWeight: "600",
                  color: isFocused ? "white" : (isComplete ? "#a78bfa" : "#64748b"),
                  whiteSpace: "nowrap"
                }}>
                  {item.label.replace('✓ ', '').replace('', '').replace('', '').replace('', '').replace('', '').replace('', '').replace('', '').split(' ')[0]}
                </span>
              </div>
            );
          })}
        </div>

        {/* Large Card - Shows ONLY the focused/current step */}
        <div style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          backgroundColor: "rgba(255, 255, 255, 0.08)",
          padding: "20px",
          borderRadius: "12px",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
          overflow: "auto",
          minHeight: 0
        }}>
          {(() => {
            // Determine which step to show
            const currentKey = focusedStep || (progressSteps.find(s => s.status === 'in_progress')?.key || progressSteps.filter(s => s.status === 'complete').slice(-1)[0]?.key || progressSteps[0].key);
            const item = progressSteps.find(s => s.key === currentKey);

            if (!item) return null;

            const isComplete = item.status === 'complete';
            const isCurrent = item.status === 'in_progress';
            const isFailed = item.status === 'failed';

            return (
              <div style={{
                display: "flex",
                flexDirection: "column",
                height: "100%"
              }}>
                {/* Header Row */}
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "8px"
                }}>
                  <div style={{
                    minWidth: "24px",
                    height: "24px",
                    borderRadius: "50%",
                    backgroundColor: isFailed ? "#ef4444" : isComplete ? "#a78bfa" : isCurrent ? "#6366f1" : "#e2e8f0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginRight: "10px",
                    fontSize: "12px",
                    flexShrink: 0,
                    transition: "all 0.3s"
                  }}>
                    <span style={{ color: isComplete || isCurrent || isFailed ? "white" : "#888" }}>
                      {isFailed ? "✗" : isComplete ? "✓" : isCurrent ? "⟳" : "○"}
                    </span>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: isFailed ? "#ef4444" : isComplete ? "#a78bfa" : isCurrent ? "#a78bfa" : "#64748b"
                    }}>
                      {item.label}
                    </div>
                    <div style={{
                      fontSize: "12px",
                      color: "#888",
                      lineHeight: "1.3"
                    }}>
                      {item.detail}
                    </div>
                  </div>

                </div>

                  {/* Live Content Display - Interview Questions */}
                  {item.key === 'interview' && (isComplete || isCurrent) && researchData?.interview?.questions && researchData.interview.questions.length > 0 && !researchData?.interview?.previewInterview && (
                    <div style={{
                      marginTop: "12px",
                      marginBottom: "20px",
                      height: "45vh",
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column"
                    }}>
                      <div style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#a78bfa",
                        marginBottom: "8px",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px"
                      }}>
                        {t('progress.interviewQuestions')}
                      </div>
                      <div
                        className="interview-questions-scroll"
                        style={{
                          fontSize: "13px",
                          color: "#ccc",
                          lineHeight: "1.6",
                          flex: "1",
                          overflow: "hidden",
                          position: "relative"
                        }}>
                        <div
                          className="questions-content"
                          style={{
                            animation: "smoothScroll 40s linear infinite",
                            paddingBottom: "20px"
                          }}>
                          {researchData.interview.questions.map((q, idx) => (
                            <div key={idx} style={{ marginBottom: "8px", opacity: 0, animation: `fadeIn 0.5s ease-in forwards ${idx * 0.1}s` }}>
                              {idx + 1}. {q}
                            </div>
                          ))}
                        </div>
                        <div style={{
                          position: "absolute",
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: "40px",
                          background: "linear-gradient(to bottom, transparent, rgba(99, 102, 241, 0.1))",
                          pointerEvents: "none"
                        }}></div>
                      </div>
                    </div>
                  )}

                  {/* Live Content Display - Persona Interview Preview (Crossfade Carousel) */}
                  {item.key === 'interview' && isCurrent && researchData?.interview?.previewInterview && (
                    <div style={{
                      marginTop: "12px",
                      marginBottom: "20px",
                      height: "45vh",
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column"
                    }}>
                      <div style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#a78bfa",
                        marginBottom: "8px",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px"
                      }}>
                        {t('progress.interviewResponses')}
                      </div>
                      {(
                    <div style={{
                      position: "relative",
                      flex: "1",
                      overflow: "hidden"
                    }}>
                      {(() => {
                        const preview = researchData.interview.previewInterview;
                        const responses = preview.responses || [];
                        const totalResponses = responses.length;
                        const personaImage = researchData.personas?.find(p => p.name === preview.personaName)?.imageUrl;

                        return responses.map((response, idx) => {
                          const isVisible = idx === currentResponseIndex % totalResponses;
                          const sentiment = response.sentiment || 'neutral';
                          const sentimentEmoji = {positive: '😊', neutral: '😐', negative: '😟'}[sentiment] || '😐';
                          const sentimentColor = {positive: '#16a34a', neutral: '#64748b', negative: '#dc2626'}[sentiment] || '#64748b';

                          return (
                            <div
                              key={idx}
                              style={{
                                position: idx === 0 ? "relative" : "absolute",
                                top: idx === 0 ? "auto" : 0,
                                left: idx === 0 ? "auto" : 0,
                                right: idx === 0 ? "auto" : 0,
                                opacity: isVisible ? 1 : 0,
                                transition: "opacity 2s ease-in-out",
                                pointerEvents: isVisible ? "auto" : "none",
                                padding: "14px",
                                backgroundColor: "rgba(255, 255, 255, 0.05)",
                                borderRadius: "8px",
                                border: "1px solid rgba(255, 255, 255, 0.1)"
                              }}
                            >
                              <div style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                marginBottom: "10px"
                              }}>
                                {personaImage && (
                                  <img
                                    src={personaImage}
                                    alt={preview.personaName}
                                    style={{
                                      width: "40px",
                                      height: "40px",
                                      borderRadius: "50%",
                                      border: "2px solid rgba(255, 255, 255, 0.2)",
                                      objectFit: "cover"
                                    }}
                                  />
                                )}
                                <div style={{
                                  fontSize: "13px",
                                  fontWeight: "600",
                                  color: "#fff"
                                }}>
                                  {preview.personaName}
                                </div>
                              </div>
                              <div style={{
                                fontSize: "12px",
                                color: "#ccc",
                                marginBottom: "8px",
                                fontWeight: "500"
                              }}>
                                Q: {response.question}
                              </div>
                              <div style={{
                                fontSize: "13px",
                                color: "#ccc",
                                lineHeight: "1.6",
                                marginBottom: "8px",
                                overflow: "hidden",
                                display: "-webkit-box",
                                WebkitLineClamp: 4,
                                WebkitBoxOrient: "vertical",
                                textOverflow: "ellipsis"
                              }}>
                                {response.response}
                              </div>
                              <div style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                fontSize: "11px"
                              }}>
                                <span style={{ color: sentimentColor, fontWeight: "600" }}>
                                  {sentimentEmoji} {t(`progress.sentiment.${sentiment}`)}
                                </span>
                                <span style={{ color: "#888" }}>
                                  • {t('progress.response', { current: idx + 1, total: totalResponses })}
                                </span>
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                    )}
                    </div>
                  )}

                  {/* Live Content Display - Survey Questions */}
                  {item.key === 'survey' && (isComplete || isCurrent) && researchData?.survey?.questions && researchData.survey.questions.length > 0 && !researchData?.survey?.preview?.sampleResponses && (
                    <div style={{
                      marginTop: "12px",
                      marginBottom: "20px",
                      height: "45vh",
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column"
                    }}>
                      <div style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#a78bfa",
                        marginBottom: "8px",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px"
                      }}>
                        {t('progress.surveyQuestions')}
                      </div>
                      <div style={{
                        flex: "1",
                        overflow: "hidden",
                        fontSize: "13px",
                        color: "#ccc",
                        lineHeight: "1.6",
                        position: "relative"
                      }}>
                        <div style={{
                          animation: "slowScroll 40s linear infinite"
                        }}>
                          {researchData.survey.questions.map((q, idx) => (
                            <div key={idx} style={{
                              marginBottom: "8px",
                              opacity: 0,
                              animation: `fadeIn 0.5s ease-in-out ${idx * 0.1}s forwards`
                            }}>
                              {idx + 1}. {q.questionText || q}
                            </div>
                          ))}
                          {/* Duplicate for seamless loop */}
                          {researchData.survey.questions.map((q, idx) => (
                            <div key={`dup-${idx}`} style={{ marginBottom: "8px" }}>
                              {idx + 1}. {q.questionText || q}
                            </div>
                          ))}
                        </div>
                        {/* Fade gradient at bottom */}
                        <div style={{
                          position: "absolute",
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: "40px",
                          background: "linear-gradient(to bottom, transparent, rgba(99, 102, 241, 0.1))",
                          pointerEvents: "none"
                        }}></div>
                      </div>
                    </div>
                  )}

                  {/* Live Content Display - Survey Response Carousel */}
                  {item.key === 'survey' && (isComplete || isCurrent) && researchData?.survey?.preview?.sampleResponses && (
                    <div style={{
                      marginTop: "12px",
                      marginBottom: "20px",
                      height: "45vh",
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column"
                    }}>
                      <div style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#a78bfa",
                        marginBottom: "8px",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px"
                      }}>
                        {t('progress.surveyResponses')}
                      </div>
                    <div style={{
                      position: "relative",
                      flex: "1",
                      overflow: "hidden"
                    }}>
                      {researchData.survey.preview.sampleResponses.map((response, idx) => {
                        const isVisible = idx === currentSurveyResponseIndex % researchData.survey.preview.sampleResponses.length;
                        const personaName = response.personaName || 'Anonymous';
                        const personaImage = researchData.personas?.find(p => p.name === personaName)?.imageUrl;

                        return (
                          <div
                            key={idx}
                            style={{
                              position: idx === 0 ? "relative" : "absolute",
                              top: idx === 0 ? "auto" : 0,
                              left: idx === 0 ? "auto" : 0,
                              right: idx === 0 ? "auto" : 0,
                              opacity: isVisible ? 1 : 0,
                              transition: "opacity 2s ease-in-out",
                              pointerEvents: isVisible ? "auto" : "none",
                              padding: "14px",
                              backgroundColor: "rgba(255, 255, 255, 0.05)",
                              borderRadius: "8px",
                              border: "1px solid rgba(255, 255, 255, 0.1)"
                            }}
                          >
                            <div style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                              marginBottom: "10px"
                            }}>
                              {personaImage && (
                                <img
                                  src={personaImage}
                                  alt={personaName}
                                  style={{
                                    width: "40px",
                                    height: "40px",
                                    borderRadius: "50%",
                                    border: "2px solid rgba(255, 255, 255, 0.2)",
                                    objectFit: "cover"
                                  }}
                                />
                              )}
                              <div style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: "#fff"
                              }}>
                                {personaName}
                              </div>
                            </div>
                            {/* Sample Answers from this persona */}
                            <div style={{
                              fontSize: "13px",
                              color: "#ccc",
                              lineHeight: "1.6",
                              marginBottom: "10px"
                            }}>
                              {(() => {
                                // Get formSchema to match questions with answers
                                const formSchema = researchData.survey.formSchema;
                                const metadataFields = ['id', 'timestamp', 'personaName', 'personaDescription', 'personaIndex', 'synthetic'];

                                // Build question map from formSchema
                                const questionMap = {};
                                if (formSchema?.pages) {
                                  formSchema.pages.forEach(page => {
                                    page.elements?.forEach(elem => {
                                      if (elem.name) {
                                        questionMap[elem.name] = {
                                          title: elem.title || elem.name,
                                          type: elem.type,
                                          choices: elem.choices
                                        };
                                      }
                                    });
                                  });
                                }

                                // Get first 3 answer pairs
                                const answerPairs = Object.entries(response)
                                  .filter(([key]) => !metadataFields.includes(key))
                                  .slice(0, 3);

                                // Helper to format answer value
                                const formatAnswer = (answer, questionData) => {
                                  // Object (matrix/complex) → Expand key-value pairs
                                  if (typeof answer === 'object' && answer !== null && !Array.isArray(answer)) {
                                    const entries = Object.entries(answer);
                                    if (entries.length === 0) return t('progress.noResponse');

                                    return entries.map(([key, value]) => {
                                      const formattedKey = key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                                      // Handle nested objects recursively
                                      const formattedValue = typeof value === 'object' && value !== null
                                        ? JSON.stringify(value)
                                        : value;
                                      return `${formattedKey}: ${formattedValue}`;
                                    }).join(', ');
                                  }

                                  // Number 1-5 or 1-10 → Stars or rating
                                  if (typeof answer === 'number') {
                                    if (answer >= 1 && answer <= 5) {
                                      const stars = '⭐'.repeat(answer);
                                      return `${stars} ${t(`progress.rating.${answer}`, { defaultValue: '' })}`;
                                    }
                                    if (answer >= 1 && answer <= 10) {
                                      return `${answer}/10`;
                                    }
                                    return answer;
                                  }

                                  // Array → Join
                                  if (Array.isArray(answer)) {
                                    return answer.join(', ');
                                  }

                                  // String → Direct display
                                  const str = String(answer);
                                  return str.length > 100 ? str.substring(0, 100) + '...' : str;
                                };

                                const getIcon = (answer) => {
                                  if (typeof answer === 'number' && answer >= 1 && answer <= 5) return '⭐';
                                  if (typeof answer === 'string' && answer.length > 30) return '';
                                  if (Array.isArray(answer)) return '✓';
                                  return '📊';
                                };

                                return answerPairs.map(([fieldName, answer], qIdx) => {
                                  const questionData = questionMap[fieldName];
                                  const questionText = questionData?.title || fieldName.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

                                  return (
                                    <div key={qIdx} style={{ marginBottom: "10px" }}>
                                      <div style={{
                                        fontWeight: "600",
                                        color: "#a78bfa",
                                        fontSize: "12px",
                                        marginBottom: "4px",
                                        display: "flex",
                                        alignItems: "flex-start",
                                        gap: "6px"
                                      }}>
                                        <span>{getIcon(answer)}</span>
                                        <span>{questionText}</span>
                                      </div>
                                      <div style={{ color: "#475569", paddingLeft: "22px", fontSize: "12px" }}>
                                        {formatAnswer(answer, questionData)}
                                      </div>
                                    </div>
                                  );
                                });
                              })()}
                            </div>

                            {/* Stats footer */}
                            <div style={{
                              fontSize: "11px",
                              color: "#888",
                              paddingTop: "8px",
                              borderTop: "1px solid rgba(99, 102, 241, 0.2)"
                            }}>
                              {researchData.survey.preview.totalResponses} {t('progress.totalResponses')} •
                              <span style={{ color: "#16a34a", marginLeft: "6px" }}>{researchData.survey.preview.sentimentDistribution?.positive || 0}%</span>
                              <span style={{ color: "#888", marginLeft: "4px" }}>{researchData.survey.preview.sentimentDistribution?.neutral || 0}%</span>
                              <span style={{ color: "#dc2626", marginLeft: "4px" }}>{researchData.survey.preview.sentimentDistribution?.negative || 0}%</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    </div>
                  )}

                  {/* Live Content Display - Meta-Analysis Preview (First Paragraph) */}
                  {item.key === 'metaAnalysis' && isCurrent && researchData?.metaAnalysis?.report && (() => {
                    console.log('Meta-analysis check:', {
                      hasMetaAnalysis: !!researchData?.metaAnalysis,
                      hasReport: !!researchData?.metaAnalysis?.report,
                      reportLength: researchData?.metaAnalysis?.report?.length
                    });
                    return true;
                  })() && (
                    <div style={{
                      marginTop: "12px",
                      marginBottom: "20px",
                      height: "45vh",
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column"
                    }}>
                      <div style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#a78bfa",
                        marginBottom: "8px",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px"
                      }}>
                        {t('progress.executiveSummaryPreview')}
                      </div>
                      <div style={{
                        fontSize: "13px",
                        color: "#ccc",
                        lineHeight: "1.6",
                        flex: "1",
                        overflow: "hidden",
                        position: "relative"
                      }}>
                        {(() => {
                          const report = researchData.metaAnalysis.report;

                          // Extract executive summary section (everything up to next ## heading)
                          const execSummaryMatch = report.match(/## Executive Summary\n\n([\s\S]*?)(?=\n##|$)/);

                          if (execSummaryMatch) {
                            const summary = execSummaryMatch[1].trim();
                            // Show up to 800 chars of executive summary
                            return summary.substring(0, 800) + (summary.length > 800 ? '...' : '');
                          }

                          // Fallback: first 2 paragraphs
                          const paragraphs = report.split('\n\n').filter(p => p.trim() && !p.startsWith('#'));
                          const preview = paragraphs.slice(0, 2).join('\n\n');
                          return preview.substring(0, 800) + (preview.length > 800 ? '...' : '');
                        })()}
                      </div>
                    </div>
                  )}
              </div>
            );
          })()}
        </div>

        {(researchData?.currentStage === 'complete' || researchData?.progress?.pdf === 'complete') && (
          <button
            onClick={() => navigate(`/ultrafastresearch/${id}/results`)}
            style={{
              width: "100%",
              padding: "14px 20px",
              fontSize: "15px",
              fontWeight: "600",
              border: "none",
              borderRadius: "10px",
              backgroundColor: "#a78bfa",
              color: "white",
              cursor: "pointer",
              transition: "all 0.2s",
              boxShadow: "0 4px 12px rgba(167, 139, 250, 0.2)",
              marginTop: "16px",
              flexShrink: 0
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#6366f1";
              e.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#a78bfa";
              e.currentTarget.style.transform = "translateY(0)";
            }}
          >
            {t('progress.viewResults')}
          </button>
        )}

      </div>
      )}
    </div>
    </>
  );
}

export default ProgressTracker;
